import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  AUTH_REQUIRED_EVENT,
  type AuthRequiredEventDetail
} from './authEvents.ts'
import {
  authFailureFromStatus,
  authGatePresentation,
  checkAuthSession,
  type AuthSessionCheckResult
} from './authSession.ts'
import { backendRuntimeConfig, resolveApiUrl } from './runtimeConfig.ts'

type AuthSessionGateProps = {
  children: ReactNode
}

export default function AuthSessionGate({ children }: AuthSessionGateProps) {
  const [attempt, setAttempt] = useState(0)
  const [result, setResult] = useState<AuthSessionCheckResult>(() => (
    backendRuntimeConfig.enabled ? { status: 'failure', failure: { kind: 'network', status: null } } : { status: 'preview' }
  ))
  const [checking, setChecking] = useState(backendRuntimeConfig.enabled)
  const sessionUrl = useMemo(
    () => backendRuntimeConfig.enabled
      ? resolveApiUrl('/v1/auth/session', backendRuntimeConfig)
      : null,
    []
  )

  useEffect(() => {
    const handleAuthRequired = (event: Event) => {
      const detail = (event as CustomEvent<AuthRequiredEventDetail>).detail
      if (detail?.failure) {
        setChecking(false)
        setResult({ status: 'failure', failure: detail.failure })
        return
      }
      if (detail?.status !== 401 && detail?.status !== 403) return
      setChecking(false)
      setResult({ status: 'failure', failure: authFailureFromStatus(detail.status) })
    }
    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired)
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired)
  }, [])

  useEffect(() => {
    if (!backendRuntimeConfig.enabled) return
    const controller = new AbortController()
    setChecking(true)
    void checkAuthSession(backendRuntimeConfig, { signal: controller.signal })
      .then(next => {
        if (!controller.signal.aborted) {
          setResult(next)
          setChecking(false)
        }
      })
      .catch(error => {
        if (!controller.signal.aborted) {
          console.error('Auth session check failed', error)
          setResult({ status: 'failure', failure: { kind: 'network', status: null } })
          setChecking(false)
        }
      })
    return () => controller.abort()
  }, [attempt])

  if (!backendRuntimeConfig.enabled || result.status === 'preview') return children
  if (!checking && result.status === 'ready') return children

  if (checking) {
    return (
      <main className="auth-session-gate">
        <section className="auth-session-card" role="status" aria-live="polite">
          <div className="auth-session-pulse" aria-hidden="true" />
          <small>SESSION CHECK</small>
          <h1>正在验证终端会话</h1>
          <p>仅检查一次 <code>GET /v1/auth/session</code>；验证成功前不会启动行情、工作区或实时连接。</p>
        </section>
      </main>
    )
  }

  const failure = result.status === 'failure'
    ? result.failure
    : { kind: 'invalid_response' as const, status: null }
  const presentation = authGatePresentation(failure)
  return (
    <main className="auth-session-gate">
      <section className="auth-session-card auth-session-error" role="alert">
        <small>{presentation.eyebrow}</small>
        <h1>{presentation.title}</h1>
        <p>{presentation.message}</p>
        <div className="auth-session-actions">
          {sessionUrl && (
            <a href={sessionUrl} target="_blank" rel="noreferrer noopener">
              {presentation.actionLabel} <span aria-hidden="true">↗</span>
            </a>
          )}
          <button type="button" onClick={() => setAttempt(value => value + 1)}>
            重新检查
          </button>
        </div>
        <footer>
          API <code>{backendRuntimeConfig.apiBaseUrl}</code>
          <span>·</span>
          未保存访问令牌
        </footer>
      </section>
    </main>
  )
}
